import { withConsoleBase } from '../config';
import { assertCurrentSessionGeneration, notifySessionExpired, sessionGeneration } from '../lib/session-events';

export const directorLaneIds = ['work', 'outreach', 'apollo', 'articles', 'campaigns', 'support'] as const;
export const directorReasons = [
  'automation_disabled', 'automation_paused', 'scheduler_unavailable', 'daily_limit',
  'contact_required', 'preview_required', 'visual_review_required', 'campaign_owned',
  'marketing_stopped', 'source_changed', 'evidence_unavailable', 'candidate_window',
  'contact_review_blocked', 'review_failed', 'rejected', 'delivery_uncertain', 'not_due',
  'enrollment_paused', 'manual_review_required', 'sender_unverified', 'mailbox_stale', 'context_changed'
] as const;
export type DirectorLaneId = typeof directorLaneIds[number];
export type DirectorReason = typeof directorReasons[number];
export interface DirectorLane {
  id: DirectorLaneId;
  state: 'enabled' | 'disabled' | 'paused' | 'unavailable';
  evidence: 'complete' | 'unavailable';
  eligible: number | null; reviewing: number | null; blocked: number | null; attention: number | null;
  oldest_pending_at: string | null; last_activity_at: string | null; next_check_at: string | null;
  limit: number | null; reasons: Array<{ code: DirectorReason; count: number }>;
  href: string; note: string;
}
export interface DirectorQueueSnapshot {
  observed_at: string;
  source: 'stored_review_queues';
  scope: 'Current stored candidates; next check is not a completion or send promise';
  scheduler: { enabled: boolean; running: boolean };
  lanes: DirectorLane[];
}

const laneHrefs: Record<DirectorLaneId, string[]> = {
  work: ['/work?status=AWAITING_DIRECTOR', '/work?status=NEEDS_ATTENTION'],
  outreach: ['/prospects?status=&outreach_status=REVIEW_QUEUE', '/prospects?status=&outreach_status=AWAITING_DIRECTOR', '/prospects?status=&outreach_status=NO_CONTACT', '/prospects?status=&outreach_status=STALE_QUEUED_PREVIEW'],
  apollo: ['/prospects?panel=apollo'], articles: ['/content?kind=CONTENT&status=NEEDS_REVIEW', '/content?kind=CONTENT&status=FAILED_ITEMS'],
  campaigns: ['/campaigns'], support: ['/emails']
};
export function validDirectorQueue(value: unknown): DirectorQueueSnapshot {
  const fail = (): never => { throw new Error('Director queue evidence could not be verified. Refresh to try again.'); };
  if (!value || typeof value !== 'object' || Array.isArray(value)) return fail();
  const v = value as DirectorQueueSnapshot;
  const stamp = (x: unknown) => typeof x === 'string' && x.length <= 40 && /T.*(?:Z|[+-]\d{2}:\d{2})$/.test(x) && Number.isFinite(Date.parse(x));
  const count = (x: unknown) => typeof x === 'number' && Number.isSafeInteger(x) && x >= 0;
  if (!stamp(v.observed_at) || v.source !== 'stored_review_queues'
    || v.scope !== 'Current stored candidates; next check is not a completion or send promise'
    || typeof v.scheduler?.enabled !== 'boolean' || typeof v.scheduler?.running !== 'boolean'
    || !Array.isArray(v.lanes) || v.lanes.length !== directorLaneIds.length) return fail();
  const seen = new Set<DirectorLaneId>();
  for (const lane of v.lanes) {
    if (!lane || !directorLaneIds.includes(lane.id) || seen.has(lane.id)
      || !['enabled', 'disabled', 'paused', 'unavailable'].includes(lane.state)
      || !['complete', 'unavailable'].includes(lane.evidence)
      || !laneHrefs[lane.id].includes(lane.href) || typeof lane.note !== 'string' || lane.note.length > 600
      || !Array.isArray(lane.reasons) || lane.reasons.length > directorReasons.length) return fail();
    seen.add(lane.id);
    for (const key of ['eligible', 'reviewing', 'blocked', 'attention'] as const) {
      if (lane.evidence === 'complete' ? !(count(lane[key]) || (key === 'reviewing' && lane[key] === null)) : lane[key] !== null) return fail();
    }
    if (lane.limit !== null && !count(lane.limit)) return fail();
    for (const key of ['oldest_pending_at', 'last_activity_at', 'next_check_at'] as const) {
      if (lane[key] !== null && !stamp(lane[key])) return fail();
    }
    const codes = new Set<DirectorReason>();
    for (const reason of lane.reasons) {
      if (!reason || !directorReasons.includes(reason.code) || !count(reason.count) || codes.has(reason.code)) return fail();
      codes.add(reason.code);
    }
  }
  // Project only this bounded aggregate contract, never retain arbitrary payload fields.
  return {
    observed_at: v.observed_at, source: v.source, scope: v.scope,
    scheduler: { enabled: v.scheduler.enabled, running: v.scheduler.running },
    lanes: v.lanes.map(lane => ({
      id: lane.id, state: lane.state, evidence: lane.evidence,
      eligible: lane.eligible, reviewing: lane.reviewing, blocked: lane.blocked, attention: lane.attention,
      oldest_pending_at: lane.oldest_pending_at, last_activity_at: lane.last_activity_at, next_check_at: lane.next_check_at,
      limit: lane.limit, reasons: lane.reasons.map(({ code, count }) => ({ code, count })), href: lane.href, note: lane.note
    }))
  };
}

export async function directorQueueSnapshot(signal?: AbortSignal): Promise<DirectorQueueSnapshot> {
  const generation = sessionGeneration();
  const failed = () => new Error('Director queue is unavailable. Refresh to try again.');
  let response: Response;
  try { response = await fetch(withConsoleBase('/console-api/v1/director-queue'), { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' }, signal }); }
  catch { assertCurrentSessionGeneration(generation); throw failed(); }
  assertCurrentSessionGeneration(generation);
  if (!response.ok) {
    if (response.status === 401) notifySessionExpired(generation);
    throw failed();
  }
  let value: unknown;
  try { value = await response.json(); } catch { assertCurrentSessionGeneration(generation); throw failed(); }
  assertCurrentSessionGeneration(generation);
  return validDirectorQueue(value);
}
